import { FC, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import { CompanySales, SaleBidders, SaleBiddersVariables } from 'query/__generated__/sales';
import { COMPANY_SALES_QUERY, SALE_BIDDERS_QUERY } from 'query/sales';

import { SaleBlotterSkeleton } from './SaleBlotterSkeleton';
import { BIDDER_STATUS_LABEL, BIDDER_STATUS_PILL, saleStatusBadge } from '../../sales/styles';

type SaleView = CompanySales['companySales'][number];
type Sale = Extract<SaleView, { __typename: 'Sale' }>;
type SaleAnnouncement = Extract<SaleView, { __typename: 'SaleAnnouncement' }>;

function isSale(view: SaleView): view is Sale {
  return view.__typename === 'Sale';
}

function isSaleAnnouncement(view: SaleView): view is SaleAnnouncement {
  return view.__typename === 'SaleAnnouncement';
}

const RequestCount: FC<{ saleId: string }> = ({ saleId }) => {
  const { data, loading, error, stopPolling } = useQuery<SaleBidders, SaleBiddersVariables>(
    SALE_BIDDERS_QUERY,
    {
      variables: { saleId },
      fetchPolicy: 'cache-and-network',
      pollInterval: 5000,
    },
  );

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  if (loading && !data) {
    return <span className="animate-pulse text-gray-500">…</span>;
  }

  // saleBidders returns every request regardless of status; APPROVED/REJECTED aren't pending
  // review, so only PENDING counts toward "requests waiting on you".
  if (error && !data) {
    return <span className="whitespace-nowrap text-right text-status-error">-- error</span>;
  }
  const count = data?.saleBidders.filter((bidder) => bidder.status === 'PENDING').length ?? 0;
  return (
    <span data-testid="sale-blotter-pending" className="whitespace-nowrap text-right">
      {count} pending
    </span>
  );
};

export const SaleBlotter: FC = () => {
  const navigate = useNavigate();
  const {
    data,
    loading,
    error: queryError,
    stopPolling,
  } = useQuery<CompanySales>(COMPANY_SALES_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000,
  });

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  if (loading && !data) {
    return <SaleBlotterSkeleton />;
  }

  // Polls every 5s; a single transient failure shouldn't blow away the blotter when the last
  // good page of data is still in hand (same reasoning as SalesIndex, SalePage, RequestCount).
  if (queryError && !data) {
    return <div>Error loading sales</div>;
  }

  const sales = data?.companySales.slice(0, 3) ?? [];

  const handleSaleClick = (saleId: string) => {
    navigate(`/sales/${saleId}`);
  };

  return (
    <>
      <h2 className="mb-4 font-heebo text-lg font-medium text-white">Sale Blotter</h2>
      <div className="flex overflow-x-auto">
        {sales.map((sale) => (
          <div
            key={sale.sale_id}
            data-testid="sale-blotter-card"
            data-sale-id={sale.sale_id}
            className="box-border max-w-[220px] flex-1 flex-shrink-0 cursor-pointer rounded border border-transparent-0 border-r-slate-500 bg-background-canvas p-3 text-white transition-[background-color,border-color,box-shadow] duration-150 last:border-r-transparent-0 hover:border-accent-default hover:bg-slate-900 hover:ring-1 hover:ring-accent-default"
            onClick={() => handleSaleClick(sale.sale_id)}
          >
            <div
              data-testid="sale-blotter-name"
              className="max-w-48 truncate overflow-ellipsis font-medium text-purple-300"
            >
              {sale.sale_name}
            </div>
            {isSale(sale) && (
              <div className="mt-1 flex max-w-48 items-center justify-between">
                <span
                  data-testid="sale-blotter-status"
                  className={`inline-block rounded px-2 py-0.5 text-center text-xs font-bold ${
                    saleStatusBadge(sale).pillClass
                  }`}
                >
                  {saleStatusBadge(sale).label}
                </span>
                {/* Only an OPEN sale takes access requests; for CREATING/PENDING_APPROVAL/FAILED,
                    polling saleBidders every 5s would only ever read "0 pending". */}
                {sale.status === 'OPEN' && <RequestCount saleId={sale.sale_id} />}
              </div>
            )}
            {isSaleAnnouncement(sale) && (
              <div className="mt-1 flex max-w-48 items-center justify-between">
                <span className="whitespace-nowrap text-left text-sm font-bold">Bidding</span>
                <span
                  data-testid="sale-blotter-bidder-status"
                  className={`inline-block rounded px-2 py-0.5 text-center text-xs font-bold ${
                    sale.bidder_status
                      ? (BIDDER_STATUS_PILL[sale.bidder_status] ?? 'bg-slate-700 text-white')
                      : 'bg-slate-700 text-gray-200'
                  }`}
                >
                  {sale.bidder_status
                    ? (BIDDER_STATUS_LABEL[sale.bidder_status] ?? sale.bidder_status)
                    : 'No request'}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default SaleBlotter;
